import React, { useState } from 'react';
import { updateUserProfile } from 'wasp/client/operations';
import { logout } from 'wasp/client/auth';
import type { User } from 'wasp/entities';
import { isAdministrator, getRoleLabel } from '../client/lib/platformRole';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { DashboardLayout } from '../client/components/Sidebar';
import { Bell, MapPin, User as UserIcon, Mail, Calendar, Shield, Palette, FileText, LogOut, ChevronRight, Clock, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useQuery,
  getMySubscribedLocations,
  getMyFollowedOrganizations,
  getMyLocationNavSettings,
  getNotificationPreferences,
  toggleLocationNavVisibility,
  updateNotificationPreferences,
} from 'wasp/client/operations';
import { Link, useSearchParams } from 'react-router-dom';
import { locationSectionPathFromLocation } from '../client/lib/locationPaths';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const ACCOUNT_TAB_VALUES = ['profile', 'subscriptions', 'notifications', 'security', 'appearance'] as const;

export default function AccountPage({ user }: { user: User }) {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const defaultTab =
    tabParam && ACCOUNT_TAB_VALUES.includes(tabParam as (typeof ACCOUNT_TAB_VALUES)[number])
      ? tabParam
      : 'profile';

  const [activeTab, setActiveTab] = useState(defaultTab);

  React.useEffect(() => {
    if (tabParam && ACCOUNT_TAB_VALUES.includes(tabParam as (typeof ACCOUNT_TAB_VALUES)[number])) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [username, setUsername] = useState(user.username || '');
  const [bio, setBio] = useState(user.bio || '');
  const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber || '');
  const [fullName, setFullName] = useState(user.fullName || '');

  const handleSaveProfile = async () => {
    try {
      setIsSaving(true);
      await updateUserProfile({
        username,
        fullName,
        phoneNumber,
        bio,
      });
      toast.success('Profile updated successfully!');
      setIsEditingProfile(false);
      // Reload the page to fetch updated user data
      window.location.reload();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update profile';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setUsername(user.username || '');
    setFullName(user.fullName || '');
    setPhoneNumber(user.phoneNumber || '');
    setBio(user.bio || '');
    setIsEditingProfile(false);
  };

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 pb-20 lg:pb-6">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Account & Profile</h1>
          <p className="text-muted-foreground mt-2">Manage your account information, profile, and preferences</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex w-full h-auto overflow-x-auto lg:grid lg:grid-cols-5 gap-1 p-1 scrollbar-none">
            <TabsTrigger value="profile" className="flex-shrink-0 min-w-[4.5rem] flex flex-col lg:flex-row items-center gap-1 py-2 px-2 lg:px-3">
              <UserIcon className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs leading-tight lg:text-sm">Profile</span>
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="flex-shrink-0 min-w-[4.5rem] flex flex-col lg:flex-row items-center gap-1 py-2 px-2 lg:px-3">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs leading-tight lg:text-sm">Subs</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex-shrink-0 min-w-[4.5rem] flex flex-col lg:flex-row items-center gap-1 py-2 px-2 lg:px-3">
              <Bell className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs leading-tight lg:text-sm">Alerts</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="flex-shrink-0 min-w-[4.5rem] flex flex-col lg:flex-row items-center gap-1 py-2 px-2 lg:px-3">
              <Shield className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs leading-tight lg:text-sm">Security</span>
            </TabsTrigger>
            <TabsTrigger value="appearance" className="flex-shrink-0 min-w-[4.5rem] flex flex-col lg:flex-row items-center gap-1 py-2 px-2 lg:px-3">
              <Palette className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs leading-tight lg:text-sm">Theme</span>
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
          {/* Account Overview Card */}
          <Card className="rounded-none shadow-none hover:shadow-none">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserIcon className="w-5 h-5" />
                Account Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-0 sm:space-y-4">
              <div className="divide-y divide-border sm:divide-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
                <div className="flex items-center justify-between gap-4 py-3 sm:block sm:py-0">
                  <label className="text-sm font-medium text-muted-foreground shrink-0">Username</label>
                  <p className="text-sm text-foreground text-right sm:text-left sm:mt-1 break-all">{user.username || 'Not set'}</p>
                </div>
                <div className="flex items-center justify-between gap-4 py-3 sm:block sm:py-0">
                  <label className="text-sm font-medium text-muted-foreground shrink-0">Email Address</label>
                  <p className="text-sm text-foreground text-right sm:text-left sm:mt-1 break-all">{user.email || 'Not set'}</p>
                </div>
                <div className="flex items-center justify-between gap-4 py-3 sm:block sm:py-0">
                  <label className="text-sm font-medium text-muted-foreground shrink-0">Account Type</label>
                  <p className="text-sm text-foreground flex items-center justify-end sm:justify-start gap-1 sm:mt-1">
                    {isAdministrator(user) ? (
                      <>
                        <Shield className="w-4 h-4 text-red-500" />
                        {getRoleLabel(user.platformRole as any)}
                      </>
                    ) : (
                      getRoleLabel(user.platformRole as any)
                    )}
                  </p>
                </div>
              </div>

              <Separator className="my-0 sm:my-4" />

              <div className="divide-y divide-border sm:divide-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
                <div className="flex items-center justify-between gap-4 py-3 sm:block sm:py-0">
                  <label className="text-sm font-medium text-muted-foreground shrink-0">Member Since</label>
                  <p className="text-sm text-foreground flex items-center justify-end sm:justify-start gap-1 sm:mt-1">
                    <Calendar className="w-4 h-4 text-muted-foreground hidden sm:inline" />
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                  </p>
                </div>
                <div className="flex items-center justify-between gap-4 py-3 sm:block sm:py-0">
                  <label className="text-sm font-medium text-muted-foreground shrink-0">Points Earned</label>
                  <p className="text-sm text-foreground text-right sm:text-left sm:mt-1">{user.pointsEarned || 0}</p>
                </div>
                <div className="flex items-center justify-between gap-4 py-3 sm:block sm:py-0">
                  <label className="text-sm font-medium text-muted-foreground shrink-0">Approval Score</label>
                  <p className="text-sm text-foreground text-right sm:text-left sm:mt-1">{user.approvalScore || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Profile Information Card */}
          <div className="grid grid-cols-1 gap-6">
            <div>
              <Card className="rounded-none shadow-none hover:shadow-none">
                <CardHeader>
                  <CardTitle>Personal Information</CardTitle>
                  <CardDescription>Update your personal details and profile</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="full-name">Full Name</Label>
                        <div className="relative">
                          <UserIcon className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="full-name"
                            className="pl-9"
                            placeholder="Enter your full name"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            disabled={!isEditingProfile}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone-number">Phone Number</Label>
                        <Input
                          id="phone-number"
                          placeholder="+1 234 567 8900"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          disabled={!isEditingProfile}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email-address">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="email-address"
                          className="pl-9"
                          type="email"
                          placeholder="your.email@example.com"
                          defaultValue={user.email || ''}
                          disabled
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">Email cannot be changed here. Contact support if needed.</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="username">Username</Label>
                      <Input
                        id="username"
                        placeholder="Enter your username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        disabled={!isEditingProfile}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bio">Bio</Label>
                      <div className="relative">
                        <FileText className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Textarea
                          id="bio"
                          className="pl-9 min-h-[100px]"
                          placeholder="Tell us about yourself"
                          value={bio}
                          onChange={(e) => setBio(e.target.value)}
                          disabled={!isEditingProfile}
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-3">
                      {isEditingProfile ? (
                        <>
                          <Button variant="outline" onClick={handleCancelEdit} disabled={isSaving}>
                            Cancel
                          </Button>
                          <Button onClick={handleSaveProfile} disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Save Changes'}
                          </Button>
                        </>
                      ) : (
                        <Button onClick={() => setIsEditingProfile(true)}>
                          Edit Profile
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <NotificationPreferencesPanel />
          </TabsContent>

          {/* Security & Privacy Tab */}
          <TabsContent value="security">
          <Card className="rounded-none shadow-none hover:shadow-none">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Privacy & Security
              </CardTitle>
              <CardDescription>
                Manage your privacy settings and security preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Profile Visibility</Label>
                  <p className="text-sm text-muted-foreground">
                    Make your profile visible to other users
                  </p>
                </div>
                <Switch defaultChecked />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Two-Factor Authentication</Label>
                  <p className="text-sm text-muted-foreground">
                    Add an extra layer of security to your account
                  </p>
                </div>
                <Button variant="outline" size="sm">
                  Enable 2FA
                </Button>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Change Password</Label>
                  <p className="text-sm text-muted-foreground">
                    Update your account password
                  </p>
                </div>
                <Button variant="outline" size="sm">
                  Change Password
                </Button>
              </div>
            </CardContent>
          </Card>
          </TabsContent>

          {/* Appearance Tab */}
          <TabsContent value="appearance">
          <Card className="rounded-none shadow-none hover:shadow-none">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Appearance
              </CardTitle>
              <CardDescription>
                Customize the look and feel of your interface
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Dark Mode</Label>
                  <p className="text-sm text-muted-foreground">
                    Switch between light and dark themes
                  </p>
                </div>
                <Switch />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Compact View</Label>
                  <p className="text-sm text-muted-foreground">
                    Use a more compact layout for better space utilization
                  </p>
                </div>
                <Switch />
              </div>
            </CardContent>
          </Card>
          </TabsContent>
          
          <TabsContent value="subscriptions">
            <Card className="rounded-none shadow-none hover:shadow-none">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Location Navigation
                </CardTitle>
                <CardDescription>
                  Choose which locations appear in the sidebar and top location picker. At least one location must stay visible.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <LocationNavVisibilityList />
              </CardContent>
            </Card>
            <Card className="rounded-none shadow-none hover:shadow-none mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Subscribed Locations
                </CardTitle>
                <CardDescription>
                  Locations you are currently subscribed to. If all children locations of a parent are subscribed, only the parent is shown.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SubscribedLocationsList />
              </CardContent>
            </Card>
            <Card className="rounded-none shadow-none hover:shadow-none">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Followed Organizations
                </CardTitle>
                <CardDescription>
                  Organizations you are currently following. You receive updates from these organizations every 4 hours.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FollowedOrganizationsList />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="mt-8 pt-6 border-t border-border">
          <Button
            variant="destructive"
            onClick={() => logout()}
            className="flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}

type DeliveryMode = 'OFF' | 'DIGEST' | 'INSTANT';
type GlobalMode = 'ACTIVE' | 'PAUSED';
type DigestFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY';
type NotificationChannel = 'EMAIL' | 'PUSH';

type NotificationPreferenceCell = {
  id: string;
  locationId: string;
  section: string;
  eventType: string;
  channel: NotificationChannel;
  mode: DeliveryMode;
};

type NotificationPreferencesResponse = {
  global: {
    globalMode: GlobalMode;
    highAlertsOptOut: boolean;
    digestFrequency: DigestFrequency;
    inAppPopupEnabled: boolean;
  };
  locations: Array<{
    locationId: string;
    locationName: string;
    preferences: NotificationPreferenceCell[];
  }>;
};

const SECTION_LABELS: Record<string, string> = {
  NOTICES: 'Notices',
  EVENTS: 'Events',
  FOOD_COURT: 'Food court',
  FACILITIES: 'Facilities',
  SERVICES: 'Services',
  BUY_SELL: 'Buy & sell',
  REQUEST_OFFER: 'Request & offer',
  LOST_FOUND: 'Lost & found',
  TRANSPORTATION: 'Transportation',
  LOCATION: 'Location info',
};

type GroupedPreferenceRow = {
  key: string;
  section: string;
  eventType: string;
  EMAIL: NotificationPreferenceCell | null;
  PUSH: NotificationPreferenceCell | null;
};

function groupNotificationPreferences(
  preferences: NotificationPreferenceCell[]
): GroupedPreferenceRow[] {
  const visible = preferences.filter((pref) => pref.eventType !== 'NEW');

  return visible.reduce<GroupedPreferenceRow[]>((rows, pref) => {
    const key = pref.section;
    let row = rows.find((r) => r.key === key);
    if (!row) {
      row = {
        key,
        section: pref.section,
        eventType: pref.eventType,
        EMAIL: null,
        PUSH: null,
      };
      rows.push(row);
    }
    row[pref.channel] = pref;
    return rows;
  }, []);
}

function getUnifiedChannelMode(
  preferences: NotificationPreferenceCell[],
  channel: NotificationChannel
): DeliveryMode | null {
  const modes = preferences
    .filter((p) => p.eventType !== 'NEW' && p.channel === channel)
    .map((p) => p.mode);
  if (modes.length === 0) return null;
  const first = modes[0];
  return modes.every((m) => m === first) ? first : null;
}

function buildChannelBulkCells(
  locationId: string,
  preferences: NotificationPreferenceCell[],
  channel: NotificationChannel,
  mode: DeliveryMode
) {
  return preferences
    .filter((p) => p.eventType !== 'NEW' && p.channel === channel)
    .map((p) => ({
      locationId,
      section: p.section,
      eventType: p.eventType,
      channel,
      mode,
    }));
}

type PreferenceCellUpdate = {
  locationId: string;
  section: string;
  eventType: string;
  channel: NotificationChannel;
  mode: DeliveryMode;
};

type DeliveryModeSelectProps = {
  value: DeliveryMode | null;
  onValueChange: (mode: DeliveryMode) => void;
  disabled?: boolean;
  mixedPlaceholder?: string;
  'aria-label': string;
  className?: string;
};

function DeliveryModeSelect({
  value,
  onValueChange,
  disabled,
  mixedPlaceholder = 'Mixed',
  'aria-label': ariaLabel,
  className,
}: DeliveryModeSelectProps) {
  return (
    <Select
      value={value ?? undefined}
      onValueChange={(mode) => onValueChange(mode as DeliveryMode)}
      disabled={disabled}
    >
      <SelectTrigger className={className ?? 'w-[130px]'} aria-label={ariaLabel}>
        <SelectValue placeholder={value ? undefined : mixedPlaceholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="OFF">Off</SelectItem>
        <SelectItem value="DIGEST">Digest</SelectItem>
        <SelectItem value="INSTANT">Instant</SelectItem>
      </SelectContent>
    </Select>
  );
}

type LocationNotificationMatrixProps = {
  locationId: string;
  preferences: NotificationPreferenceCell[];
  saving: boolean;
  onUpdateCell: (cell: PreferenceCellUpdate) => void;
  onUpdateChannel: (cells: PreferenceCellUpdate[]) => void;
};

function LocationNotificationMatrix({
  locationId,
  preferences,
  saving,
  onUpdateCell,
  onUpdateChannel,
}: LocationNotificationMatrixProps) {
  const groupedRows = groupNotificationPreferences(preferences || []);
  const visiblePreferences = (preferences || []).filter((p) => p.eventType !== 'NEW');

  const applyChannelMode = (channel: NotificationChannel, mode: DeliveryMode) => {
    const cells = buildChannelBulkCells(locationId, visiblePreferences, channel, mode);
    if (cells.length > 0) {
      onUpdateChannel(cells);
    }
  };

  return (
    <div className="overflow-x-auto border rounded-none">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr>
            <th scope="col" className="text-left p-2 font-medium">
              Section
            </th>
            <th scope="col" className="text-left p-2 font-medium">
              Email
            </th>
            <th scope="col" className="text-left p-2 font-medium">
              Push
            </th>
          </tr>
          <tr className="border-t border-border/60 bg-muted/30">
            <th scope="row" className="text-left p-2 align-middle">
              <span className="text-xs font-normal text-muted-foreground">All sections</span>
            </th>
            {(['EMAIL', 'PUSH'] as const).map((channel) => (
              <th key={channel} scope="col" className="p-2 text-left font-normal align-middle">
                <DeliveryModeSelect
                  value={getUnifiedChannelMode(visiblePreferences, channel)}
                  mixedPlaceholder="Set all…"
                  onValueChange={(mode) => applyChannelMode(channel, mode)}
                  disabled={saving}
                  aria-label={`Set all ${channel === 'EMAIL' ? 'email' : 'push'} notifications for this location`}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groupedRows.map((row) => (
            <tr key={row.key} className="border-t">
              <th scope="row" className="p-2 text-left font-normal">
                {SECTION_LABELS[row.section] || row.section}
              </th>
              {(['EMAIL', 'PUSH'] as const).map((channel) => (
                <td key={channel} className="p-2">
                  <DeliveryModeSelect
                    value={row[channel]?.mode ?? 'DIGEST'}
                    onValueChange={(mode) =>
                      onUpdateCell({
                        locationId,
                        section: row.section,
                        eventType: row.eventType,
                        channel,
                        mode,
                      })
                    }
                    disabled={saving}
                    aria-label={`${SECTION_LABELS[row.section] || row.section} ${channel === 'EMAIL' ? 'email' : 'push'}`}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NotificationPreferencesPanel() {
  const { data: rawData, isLoading, error, refetch } = useQuery(getNotificationPreferences, {});
  const data = rawData as NotificationPreferencesResponse | undefined;
  const [saving, setSaving] = React.useState(false);
  const [pushPermission, setPushPermission] = React.useState('unsupported');
  const [activeLocationId, setActiveLocationId] = React.useState('');

  React.useEffect(() => {
    const locations = data?.locations ?? [];
    if (locations.length === 0) return;
    setActiveLocationId((current) =>
      locations.some((l) => l.locationId === current) ? current : locations[0].locationId
    );
  }, [data?.locations]);

  React.useEffect(() => {
    if (typeof Notification === 'undefined') {
      setPushPermission('unsupported');
      return;
    }
    setPushPermission(Notification.permission);
  }, []);

  const updateGlobal = async (patch: {
    globalMode?: GlobalMode;
    highAlertsOptOut?: boolean;
    digestFrequency?: DigestFrequency;
    inAppPopupEnabled?: boolean;
  }) => {
    try {
      setSaving(true);
      await updateNotificationPreferences(patch);
      await refetch();
      toast.success('Notification settings updated');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update notification settings';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const updatePreferences = async (
    preferences: PreferenceCellUpdate[],
    successMessage?: string
  ) => {
    if (preferences.length === 0) return;
    try {
      setSaving(true);
      await updateNotificationPreferences({ preferences });
      await refetch();
      if (successMessage) {
        toast.success(successMessage);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update preference';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const updateCell = (cell: PreferenceCellUpdate) => {
    void updatePreferences([cell]);
  };

  const updateChannel = (cells: PreferenceCellUpdate[]) => {
    const channel = cells[0]?.channel;
    const channelLabel = channel === 'EMAIL' ? 'Email' : 'Push';
    void updatePreferences(cells, `Updated all ${channelLabel} sections for this location`);
  };

  const requestPushPermission = async () => {
    if (typeof Notification === 'undefined') {
      toast.error('Push notifications are not supported in this browser');
      return;
    }
    const permission = await Notification.requestPermission();
    setPushPermission(permission);
    if (permission === 'granted') toast.success('Push permission granted');
  };

  if (isLoading) {
    return (
      <Card className="rounded-none shadow-none hover:shadow-none">
        <CardContent className="py-6 text-center text-muted-foreground">
          Loading notification preferences...
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="rounded-none shadow-none hover:shadow-none">
        <CardContent className="py-6 text-center text-destructive">
          Failed to load notification preferences.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-none shadow-none hover:shadow-none">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Notification Preferences
        </CardTitle>
        <CardDescription>
          Configure global behavior, digest frequency, high-alert opt-out, and per-location settings.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label>Pause all notifications</Label>
            <p className="text-sm text-muted-foreground">
              Pauses all non-Very-High alerts while preserving your existing matrix settings.
            </p>
          </div>
          <Switch
            checked={data.global.globalMode === 'PAUSED'}
            disabled={saving}
            onCheckedChange={(checked) =>
              updateGlobal({ globalMode: checked ? 'PAUSED' : 'ACTIVE' })
            }
          />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label>Digest frequency</Label>
            <p className="text-sm text-muted-foreground">How often DIGEST-mode notifications are emailed.</p>
          </div>
          <Select
            value={data.global.digestFrequency}
            onValueChange={(v) => updateGlobal({ digestFrequency: v as DigestFrequency })}
            disabled={saving}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Select frequency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="DAILY">Daily</SelectItem>
              <SelectItem value="WEEKLY">Weekly</SelectItem>
              <SelectItem value="MONTHLY">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label>In-app pop-up alerts</Label>
            <p className="text-sm text-muted-foreground">
              Show a brief pop-up in the top-right corner when a new notification arrives while you
              are browsing.
            </p>
          </div>
          <Switch
            checked={data.global.inAppPopupEnabled}
            disabled={saving}
            onCheckedChange={(checked) => updateGlobal({ inAppPopupEnabled: checked })}
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label>Opt out of High instant alerts</Label>
            <p className="text-sm text-muted-foreground">
              High notices follow your matrix when enabled. Very High always bypasses this.
            </p>
          </div>
          <Switch
            checked={data.global.highAlertsOptOut}
            disabled={saving}
            onCheckedChange={(checked) => updateGlobal({ highAlertsOptOut: checked })}
          />
        </div>

        <div className="border rounded-none p-4 space-y-2">
          <Label>Push permission</Label>
          <p className="text-sm text-muted-foreground">Browser status: {pushPermission}</p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={requestPushPermission}
              disabled={saving || pushPermission === 'granted'}
            >
              Request permission
            </Button>
            {pushPermission === 'denied' && (
              <span className="text-xs text-muted-foreground">
                Re-enable notifications in browser site settings.
              </span>
            )}
          </div>
        </div>

        <Separator />

        <div className="space-y-3">
          <div className="space-y-0.5">
            <Label>Per-location settings</Label>
            <p className="text-sm text-muted-foreground">
              Email and push preferences for each location. Use the &ldquo;All sections&rdquo; row to
              apply one setting to every section for that channel at once.
            </p>
          </div>

          {data.locations.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No subscribed locations. Subscribe to a location to configure notifications.
            </p>
          ) : data.locations.length === 1 ? (
            <div className="space-y-3">
              <h3 className="font-semibold text-foreground">{data.locations[0].locationName}</h3>
              <LocationNotificationMatrix
                locationId={data.locations[0].locationId}
                preferences={data.locations[0].preferences}
                saving={saving}
                onUpdateCell={updateCell}
                onUpdateChannel={updateChannel}
              />
            </div>
          ) : (
            <Tabs value={activeLocationId} onValueChange={setActiveLocationId} className="w-full">
              <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
                {data.locations.map((location) => (
                  <TabsTrigger
                    key={location.locationId}
                    value={location.locationId}
                    className="max-w-[200px] truncate"
                  >
                    {location.locationName}
                  </TabsTrigger>
                ))}
              </TabsList>
              {data.locations.map((location) => (
                <TabsContent key={location.locationId} value={location.locationId} className="mt-4">
                  <LocationNotificationMatrix
                    locationId={location.locationId}
                    preferences={location.preferences}
                    saving={saving}
                    onUpdateCell={updateCell}
                    onUpdateChannel={updateChannel}
                  />
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function LocationNavVisibilityList() {
  const { data: locations, isLoading, error, refetch } = useQuery(getMyLocationNavSettings);
  const [savingId, setSavingId] = React.useState<string | null>(null);

  const visibleCount = (locations ?? []).filter((loc: any) => loc.isVisibleInNav).length;

  const handleToggleVisibility = async (locationId: string, nextVisible: boolean) => {
    setSavingId(locationId);
    try {
      await toggleLocationNavVisibility({ locationId, isVisibleInNav: nextVisible });
      await refetch();
      toast.success(nextVisible ? 'Location shown in navigation' : 'Location hidden from navigation');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update location visibility';
      toast.error(message);
    } finally {
      setSavingId(null);
    }
  };

  if (isLoading) return <div className="py-4 text-center text-muted-foreground">Loading locations...</div>;
  if (error) return <div className="py-4 text-center text-destructive">Error loading locations</div>;
  if (!locations || locations.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        No locations available to configure.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {locations.map((loc: any) => {
        const isLastVisible = loc.isVisibleInNav && visibleCount <= 1;
        return (
          <div
            key={loc.id}
            className="flex items-center justify-between gap-4 p-4 border border-border bg-card"
          >
            <div className="min-w-0">
              <p className="font-medium text-foreground truncate">{loc.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {loc.isSubscribed ? 'Subscribed' : 'Public location'}
                {loc.isHub ? ' · Hub' : ''}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <Label htmlFor={`nav-visible-${loc.id}`} className="text-sm text-muted-foreground">
                {loc.isVisibleInNav ? 'Visible' : 'Hidden'}
              </Label>
              <Switch
                id={`nav-visible-${loc.id}`}
                checked={loc.isVisibleInNav}
                disabled={savingId === loc.id || isLastVisible}
                onCheckedChange={(checked) => handleToggleVisibility(loc.id, checked)}
              />
            </div>
          </div>
        );
      })}
      {visibleCount <= 1 && (
        <p className="text-xs text-muted-foreground">
          Your last visible location cannot be hidden.
        </p>
      )}
    </div>
  );
}

function FollowedOrganizationsList() {
  const { data: memberships, isLoading, error } = useQuery(getMyFollowedOrganizations);

  if (isLoading) return <div className="py-4 text-center text-muted-foreground">Loading followed organizations...</div>;
  if (error) return <div className="py-4 text-center text-destructive">Error loading organizations</div>;
  if (!memberships || memberships.length === 0) {
    return (
      <div className="py-12 text-center border-2 border-dashed border-muted rounded-lg">
        <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-20" />
        <h3 className="text-lg font-medium text-foreground">No Organizations Followed</h3>
        <p className="text-muted-foreground mt-1 max-w-xs mx-auto">
          You haven't followed any organizations yet. Browse organizations to start receiving updates.
        </p>
        <Link to="/search" className="inline-block mt-4">
          <Button variant="outline">Search Organizations</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3 mt-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {memberships.map((m: any) => (
          <Link 
            key={m.id} 
            to={`/${m.organization.slug}`}
            className="group block p-4 border border-border bg-card hover:bg-accent/50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 text-primary rounded-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Users className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                    {m.organization.name}
                  </h4>
                  <p className="text-xs text-muted-foreground uppercase tracking-widest mt-0.5">
                    {m.organization.cityName || 'Organization'}
                  </p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function SubscribedLocationsList() {
  const { data: locations, isLoading, error } = useQuery(getMySubscribedLocations);

  if (isLoading) return <div className="py-4 text-center text-muted-foreground">Loading subscriptions...</div>;
  if (error) return <div className="py-4 text-center text-destructive">Error loading subscriptions</div>;
  if (!locations || locations.length === 0) {
    return (
      <div className="py-12 text-center border-2 border-dashed border-muted rounded-lg">
        <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-20" />
        <h3 className="text-lg font-medium text-foreground">No Subscriptions Yet</h3>
        <p className="text-muted-foreground mt-1 max-w-xs mx-auto">
          You haven't subscribed to any locations yet. Browse locations to start receiving updates.
        </p>
        <Link to="/locations" className="inline-block mt-4">
          <Button variant="outline">Browse Locations</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3 mt-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {locations.map((loc: any) => (
          <Link 
            key={loc.id} 
            to={locationSectionPathFromLocation(loc, 'information-desk')}
            className="group block p-4 border border-border bg-card hover:bg-accent/50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 text-primary rounded-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                    {loc.name}
                  </h4>
                  <p className="text-xs text-muted-foreground uppercase tracking-widest mt-0.5">
                    {loc.locationCode}
                  </p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}



